import { Link } from 'react-router-dom';
import type { Video } from '@/mocks/videos';
import { typeLabels } from '@/mocks/videos';
import { useFavorites } from '@/hooks/useFavorites';

interface VideoCardProps {
  video: Video;
  showType?: boolean;
}

export default function VideoCard({ video, showType = true }: VideoCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(video.id);

  const typeBadge =
    video.type === 'apple' && video.appleCategory
      ? video.appleCategory
      : typeLabels[video.type];

  return (
    <div className="group flex flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200">
      <Link
        to={`/detail/${video.id}`}
        className="relative block aspect-video w-full overflow-hidden bg-background-200"
        aria-label={video.title}
      >
        <img
          src={video.cover}
          alt={`${video.title} 视频封面`}
          title={`${video.title} - 苹果视频影视资源`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent" />
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-foreground-950/70 px-2 py-0.5 text-xs text-background-50 backdrop-blur-sm">
          <i className="ri-time-line text-xs" aria-hidden="true" />
          {video.duration}
        </span>
        <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-md bg-accent-500/90 px-2 py-0.5 text-xs font-medium text-background-50">
          {typeBadge}
        </span>
        <span className="absolute inset-0 m-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-50/85 text-primary-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <i className="ri-play-fill text-2xl" aria-hidden="true" />
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-3 md:p-4">
        <div className="mb-1.5 flex items-start justify-between gap-2">
          <Link
            to={`/detail/${video.id}`}
            className="line-clamp-1 flex-1 text-sm font-bold text-foreground-950 transition-colors hover:text-primary-600 md:text-base"
          >
            {video.title}
          </Link>
          <button
            type="button"
            onClick={() => toggleFavorite(video.id)}
            aria-label={favorited ? '取消收藏' : '收藏'}
            className={`flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors ${
              favorited
                ? 'bg-accent-100 text-accent-600'
                : 'text-foreground-400 hover:bg-background-200 hover:text-accent-500'
            }`}
          >
            <i
              className={favorited ? 'ri-bookmark-fill text-sm' : 'ri-bookmark-line text-sm'}
              aria-hidden="true"
            />
          </button>
        </div>

        <p className="mb-3 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
          {video.shortDesc}
        </p>

        <div className="mt-auto flex items-center justify-between text-xs text-foreground-500">
          <div className="flex flex-wrap items-center gap-1.5">
            {video.genres.slice(0, 2).map((genre) => (
              <span
                key={genre}
                className="rounded-md bg-secondary-100 px-1.5 py-0.5 text-secondary-900"
              >
                {genre}
              </span>
            ))}
          </div>
          {showType && (
            <span className="flex items-center gap-1 text-accent-600">
              <i className="ri-star-fill text-xs" aria-hidden="true" />
              {video.rating.toFixed(1)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}